import { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { format, parseISO } from "date-fns";
import {
  ArrowLeft, RefreshCw, Link2, Clock,
  ExternalLink, AlertCircle, X, AlertTriangle,
  ChevronRight, Mail, Flag,
} from "lucide-react";
import { getAllCachedStories } from "@/lib/cache";
import { isIndianSource, getSourceType, prettifySourceName } from "@/lib/sourceUtils";
import { CompanyLogo } from "@/components/CompanyLogo";
import { API } from "@/App";
import { StructuredTakeaway } from "@/components/StructuredTakeaway";
import { getSentiment, getSimplifiedSentiment } from "@/utils/sentiment";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (iso) => {
  if (!iso) return "N/A";
  try { return format(parseISO(iso), "MMM d, yyyy 'at' h:mm a"); } catch { return iso; }
};

// First 3 sentences for the brief tab
const getBriefText = (text) => {
  if (!text) return "";
  const sentences = text.match(/[^.!?]+[.!?]+/g) || [];
  return sentences.slice(0, 3).join(" ") || text.slice(0, 500);
};

// Regex fallback for parsing diligence questions
const parseDiligenceQuestions = (text) => {
  if (!text?.trim()) return [];
  const numbered = text.match(/\d+[.)]\s+[^\n\d][^\n]+/g);
  if (numbered?.length >= 2) return numbered.map(q => q.replace(/^\d+[.)]\s+/, "").trim());
  const byQ = text.split(/\?\s+/).map(q => q.trim()).filter(q => q.length > 10);
  if (byQ.length >= 2) return byQ.map(q => q.endsWith("?") ? q : q + "?");
  const byLine = text.split("\n").map(l => l.replace(/^[-•*\s]+/, "").trim()).filter(l => l.length > 10);
  if (byLine.length >= 2) return byLine;
  return [text.trim()];
};

// Deduplicate by source_name, keep highest article_id
const deduplicateSources = (articles) => {
  const seen = {};
  for (const art of (articles || [])) {
    const src = (art.source_name || "Unknown").toLowerCase();
    if (!seen[src] || (art.article_id || 0) > (seen[src].article_id || 0)) seen[src] = art;
  }
  return Object.values(seen).sort((a, b) => (b.article_id || 0) - (a.article_id || 0));
};

const truncate = (str, n) => str && str.length > n ? str.slice(0, n) + "…" : str;

function extractDomain(url) {
  if (!url) return "—";
  try { return new URL(url).hostname.replace(/^www\./, ""); } catch { return "—"; }
}

// ─── Sentiment Badge Styles ────────────────────────────────────────────────────

const SENTIMENT_STYLES = {
  POSITIVE:   { bg: "#DCFCE7", color: "#16A34A", label: "Positive" },
  NEGATIVE:   { bg: "#FEE2E2", color: "#DC2626", label: "Negative" },
  NEUTRAL:    { bg: "#F3F4F6", color: "#6B7280", label: "Neutral" },
  CAUTIONARY: { bg: "#FEF3C7", color: "#D97706", label: "Cautionary" },
  MIXED:      { bg: "#DBEAFE", color: "#2563EB", label: "Mixed" },
};

// ─── Portfolio Impact Level Mapping ─────────────────────────────────────────────

const IMPACT_LEVEL_STYLES = {
  LOW:      { bg: "#FFFFFF", color: "#B45309", border: "#F59E0B" },
  MEDIUM:   { bg: "#FFFFFF", color: "#B45309", border: "#F59E0B" },
  HIGH:     { bg: "#FFFFFF", color: "#B45309", border: "#F59E0B" },
  CRITICAL: { bg: "#FFFFFF", color: "#B45309", border: "#F59E0B" },
};

// Determine impact level based on portfolio companies count and sentiment
function getPortfolioImpactLevel(portfolioImpact, sentiment) {
  const count = portfolioImpact?.length || 0;
  if (count === 0) return "LOW";
  
  // Check for critical keywords in impact descriptions
  const hasHighImpactKeywords = portfolioImpact.some(p => {
    const text = (p.impact_description || p.value || "").toLowerCase();
    return text.includes("significant") || text.includes("major") || 
           text.includes("critical") || text.includes("severe") ||
           text.includes("substantial") || text.includes("material");
  });
  
  if (sentiment === "NEGATIVE" && (count >= 3 || hasHighImpactKeywords)) return "CRITICAL";
  if (sentiment === "NEGATIVE" || hasHighImpactKeywords) return "HIGH";
  if (count >= 2) return "MEDIUM";
  return "LOW";
}

// ─── Story Info Badges Component ────────────────────────────────────────────────

function StoryInfoBadges({ story, hidePortfolioImpact = false, isFromWatchlist = false, portfolioAnalysis = null }) {
  // Map news category from primary_tag (strip geo suffix, match to fixed list)
  const NEWS_CATEGORIES_LIST = [
    "Deals", "Valuations", "Leadership", "Governance", "Macroeconomic",
    "Earnings Results", "Regulatory / Policy", "Strategy Updates",
    "Operational Updates", "Competitive Dynamics", "Market / Industry Trends",
    "Capital Allocation", "Capital Structure / Financing", "Exit & Liquidity",
  ];
  // Raw API tag → curated category fallback mapping
  const RAW_TO_CURATED = {
    "general news": null,
    "investment or funding related news": "Capital Allocation",
    "investment or funding": "Capital Allocation",
    "investment": "Capital Allocation",
    "funding": "Capital Allocation",
    "venture capital": "Capital Allocation",
    "market update": "Market / Industry Trends",
    "market news": "Market / Industry Trends",
    "industry trends": "Market / Industry Trends",
    "earnings": "Earnings Results",
    "earnings report": "Earnings Results",
    "quarterly results": "Earnings Results",
    "financial results": "Earnings Results",
    "regulatory": "Regulatory / Policy",
    "policy": "Regulatory / Policy",
    "regulation": "Regulatory / Policy",
    "merger": "Deals",
    "acquisition": "Deals",
    "m&a": "Deals",
    "deal": "Deals",
    "ipo": "Exit & Liquidity",
    "exit": "Exit & Liquidity",
    "listing": "Exit & Liquidity",
    "leadership change": "Leadership",
    "management change": "Leadership",
    "ceo": "Leadership",
    "strategy": "Strategy Updates",
    "operational": "Operational Updates",
    "competitive": "Competitive Dynamics",
    "capital structure": "Capital Structure / Financing",
    "financing": "Capital Structure / Financing",
    "debt": "Capital Structure / Financing",
    "governance": "Governance",
    "valuation": "Valuations",
    "macroeconomic": "Macroeconomic",
    "macro": "Macroeconomic",
  };
  const mappedCategory = useMemo(() => {
    const pt = (story.primary_tag || "").trim();
    // Strip geo suffix (e.g., "Macroeconomic - Global" → "Macroeconomic")
    const base = pt.includes(" - ") ? pt.split(" - ")[0].trim() : pt;
    // 1. Try exact match against curated list
    const exact = NEWS_CATEGORIES_LIST.find(c => c.toLowerCase() === base.toLowerCase());
    if (exact) return exact;
    // 2. Try raw→curated keyword mapping
    const rawKey = base.toLowerCase().trim();
    if (rawKey in RAW_TO_CURATED) return RAW_TO_CURATED[rawKey];
    // 3. Try partial/contains match
    for (const [raw, curated] of Object.entries(RAW_TO_CURATED)) {
      if (rawKey.includes(raw) || raw.includes(rawKey)) return curated;
    }
    // 4. No match — hide the badge
    return null;
  }, [story.primary_tag]);

  // Map industry via keyword matching (same logic as Dashboard cards)
  const INDUSTRY_KW = {
    "AI / Tech":        /\b(artificial intelligence|machine learning|deep learning|llm|gpu|semiconductor|cloud computing|data center|openai|google ai|microsoft ai|meta ai|nvidia|bytedance|scale.ai|cohere|perplexity|glean|arm holdings)\b/i,
    "E-Commerce":       /\b(e.commerce|ecommerce|online retail|marketplace|food delivery|last.mile delivery|doordash|coupang|flipkart|amazon|shopify|instacart|grubhub)\b/i,
    "Biotech / Health":  /\b(biotech|pharmaceutical|drug discovery|clinical trial|medical device|hospital|healthcare|gene therapy|genomic|vaccine|fda approval|metsera|recursion|glp.1|oncology)\b/i,
    "Fintech":          /\b(fintech|digital payment|neobank|insurtech|digital lending|credit card|digital wallet|stripe|brex|ramp|checkout\.com|blockchain|cryptocurrency|bitcoin)\b/i,
    "Deep Tech":        /\b(quantum computing|nuclear fusion|robotics|autonomous vehicle|self.driving|waymo|helion|satellite|lidar|3d printing|advanced materials|superconductor)\b/i,
    "SaaS / B2B":       /\b(saas|enterprise software|crm platform|erp system|cloud service|subscription software|databricks|snowflake|salesforce|servicenow|wiz|anduril)\b/i,
    "Consumer":         /\b(consumer goods|consumer brand|consumer product|cpg|fmcg|food and beverage|fashion brand|apparel|restaurant chain|hospitality|hotel chain|travel booking|tourism|oyo rooms|travelperk|airbnb|uber)\b/i,
    "Energy":           /\b(oil and gas|lng terminal|crude oil|petroleum|solar energy|wind energy|renewable energy|battery storage|electric vehicle|power grid|carbon emission|climate change|vertical farm|fertilizer)\b/i,
    "Industrial":       /\b(industrial manufacturing|supply chain|freight shipping|port infrastructure|construction|mining|steel production|cement|aerospace|aviation|airline)\b/i,
    "Defence":          /\b(defence|defense contract|military|weapon system|missile|nato|pentagon|armed forces|navy|air force|surveillance|cybersecurity)\b/i,
    "Real Estate":      /\b(real estate|property market|housing|mortgage|commercial real estate|reit|office space|residential|pacific union|redfin|zillow|clutter)\b/i,
  };
  const CO_MAP = {
    "openai": ["AI / Tech"], "perplexity": ["AI / Tech"], "glean": ["AI / Tech"], "cohere": ["AI / Tech"],
    "scale ai": ["AI / Tech"], "bytedance": ["AI / Tech"], "arm holdings": ["AI / Tech"], "arm": ["AI / Tech"],
    "doordash": ["E-Commerce"], "coupang": ["E-Commerce"], "flipkart": ["E-Commerce"],
    "metsera": ["Biotech / Health"], "recursion": ["Biotech / Health"],
    "stripe": ["Fintech"], "brex": ["Fintech"], "ramp": ["Fintech"],
    "waymo": ["Deep Tech"], "helion": ["Deep Tech"],
    "wiz": ["SaaS / B2B"], "anduril": ["SaaS / B2B", "Defence"],
    "oyo": ["Consumer"], "travelperk": ["Consumer"],
    "plenty": ["Energy"], "plenty unlimited": ["Energy"],
    "pacific union": ["Real Estate"], "clutter": ["Real Estate"],
  };
  const matchedIndustries = useMemo(() => {
    const text = `${story.title || ""} ${story.snippet || ""}`;
    const matched = new Set();
    (story.portfolio_impact || []).forEach(p => {
      const key = (p.company_name || "").toLowerCase().trim();
      (CO_MAP[key] || []).forEach(ind => matched.add(ind));
    });
    for (const [vertical, regex] of Object.entries(INDUSTRY_KW)) {
      if (regex.test(text)) matched.add(vertical);
    }
    return [...matched];
  }, [story.title, story.snippet, story.portfolio_impact]);
  
  // Keyword sentiment using title + snippet ONLY (fallback)
  const keywordSentiment = useMemo(() => {
    const text = `${story.title || ""} ${story.snippet || ""}`;
    return getSimplifiedSentiment(getSentiment(text));
  }, [story.title, story.snippet]);

  const portfolioImpact = story.portfolio_impact || [];
  const hasPortfolio = portfolioImpact.length > 0;

  // Use portfolio analysis sentiment as primary; fall back to keyword
  const sentiment = hasPortfolio && portfolioAnalysis?.sentiment
    ? portfolioAnalysis.sentiment
    : keywordSentiment;
  
  // Determine portfolio impact level
  const impactLevel = useMemo(() => 
    getPortfolioImpactLevel(portfolioImpact, sentiment),
    [portfolioImpact, sentiment]
  );
  
  // Simplified sentiment styles (only 3 options)
  const sentimentStyles = {
    POSITIVE: { dotColor: "#16A34A", label: "Positive" },
    NEUTRAL:  { dotColor: "#6B7280", label: "Neutral" },
    NEGATIVE: { dotColor: "#DC2626", label: "Negative" },
  };
  
  const sentimentStyle = sentimentStyles[sentiment] || sentimentStyles.NEUTRAL;
  const impactStyle = IMPACT_LEVEL_STYLES[impactLevel] || IMPACT_LEVEL_STYLES.LOW;
  const showWarningIcon = impactLevel === "HIGH" || impactLevel === "CRITICAL" || impactLevel === "MEDIUM";
  
  return (
    <div className="flex flex-wrap items-center gap-3 mb-5" data-testid="story-info-badges">
      {/* News Category Badge - only show for curated/mapped categories */}
      {mappedCategory && (
      <div 
        className="inline-flex items-center rounded-md text-xs font-semibold uppercase tracking-wide"
        style={{ 
          background: "#EEF4FF", 
          color: "#3B6CF5",
          border: "1px solid #BFDBFE",
          padding: "8px 14px",
          letterSpacing: "0.05em",
        }}
        data-testid="badge-category"
      >
        {mappedCategory.toUpperCase()}
      </div>
      )}

      {/* Industry Badges - Light gray */}
      {matchedIndustries.map(ind => (
        <div 
          key={ind}
          className="inline-flex items-center rounded-md text-xs font-semibold uppercase tracking-wide"
          style={{ 
            background: "#F3F4F6", 
            color: "#4B5563",
            border: "1px solid #D1D5DB",
            padding: "8px 14px",
            letterSpacing: "0.05em",
          }}
          data-testid={`badge-industry-${ind}`}
        >
          {ind.toUpperCase()}
        </div>
      ))}
      
      {/* Sentiment Badge - White with gray border */}
      <div 
        className="inline-flex items-center gap-2 rounded-md text-xs font-semibold uppercase tracking-wide"
        style={{ 
          background: "#FFFFFF", 
          color: "#374151",
          border: "1px solid #E5E7EB",
          padding: "8px 14px",
          letterSpacing: "0.05em",
        }}
        data-testid="badge-sentiment"
      >
        <span 
          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
          style={{ background: sentimentStyle.dotColor }}
        />
        <span>SENTIMENT: <span style={{ color: sentimentStyle.dotColor }}>{sentimentStyle.label.toUpperCase()}</span></span>
      </div>
      
      {/* Portfolio Impact Badge - hidden for watchlist stories */}
      {!isFromWatchlist && (
      <div 
        className="inline-flex items-center gap-2 rounded-md text-xs font-semibold uppercase tracking-wide"
        style={{ 
          background: impactStyle.bg, 
          color: impactStyle.color,
          border: `1px solid ${impactStyle.border}`,
          padding: "8px 14px",
          letterSpacing: "0.05em",
        }}
        data-testid="badge-impact"
      >
        {showWarningIcon && <AlertTriangle size={14} className="flex-shrink-0" />}
        <span>PORTFOLIO IMPACT: {impactLevel}</span>
      </div>
      )}
    </div>
  );
}

// ─── Section Label ─────────────────────────────────────────────────────────────

function SectionLabel({ children }) {
  return (
    <div style={{ borderBottom: "1px solid #DCDFE2", marginBottom: "16px", paddingBottom: "8px" }}>
      <span style={{ fontSize: "11px", fontWeight: 600, color: "#9AA0A6", letterSpacing: "0.08em", textTransform: "uppercase" }}>
        {children}
      </span>
    </div>
  );
}

// ─── Formatted Summary (Claude-powered HTML) ───────────────────────────────────

function FormattedSummary({ text, type, storyId }) {
  const [html, setHtml] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!text?.trim()) { setLoading(false); return; }
    setLoading(true);
    setHtml(null);
    fetch(`${API}/format/summary`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ content: text, type, story_id: storyId }),
    })
      .then(r => r.json())
      .then(data => { setHtml(data.html || ""); setLoading(false); })
      .catch(() => { setHtml(""); setLoading(false); });
  }, [text, type, storyId]);

  if (loading) {
    return (
      <div className="space-y-2 animate-pulse" data-testid="summary-loading">
        {[100, 85, 72, 90, 65].map((w, i) => (
          <div key={i} className="h-3 rounded" style={{ width: `${w}%`, background: "#E5E7EB" }} />
        ))}
      </div>
    );
  }

  return (
    <div
      className="formatted-html-content text-sm leading-relaxed"
      style={{ color: "#374151" }}
      dangerouslySetInnerHTML={{ __html: html || text || "" }}
      data-testid="summary-content"
    />
  );
}

// ─── Portfolio Impact Card (receives analysis result as prop) ──────────────────

function PortfolioImpactCard({ impacts, analysis }) {
  const primaryImpact = impacts[0];
  const style = analysis ? (SENTIMENT_STYLES[analysis.sentiment] || SENTIMENT_STYLES.NEUTRAL) : null;

  return (
    <div className="rounded-md bg-white" style={{ border: "1px solid #DCDFE2", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden", minHeight: "90px" }}>
      {/* Header: logo + name + sentiment */}
      <div className="px-4 pt-3 pb-3" style={{ borderBottom: "1px solid #DCDFE2", background: "#FFFFFF" }}>
        <div className="flex items-center gap-3 mb-2">
          <CompanyLogo
            name={primaryImpact.company_name}
            permalink={primaryImpact.company_permalink || (primaryImpact.company_name || "").toLowerCase().replace(/\s+/g, "-")}
            size={24}
          />
          <p className="font-bold text-[#0A0A0A] truncate flex-1 min-w-0"
            style={{ fontFamily: "'Playfair Display', serif", fontSize: "14px", lineHeight: "1.3" }}>
            {primaryImpact.company_name}
          </p>
        </div>
        {style ? (
          <span className="inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded"
            style={{ background: style.bg, color: style.color }}>
            {style.label}
          </span>
        ) : (
          <div className="animate-pulse">
            <div className="h-5 w-20 rounded" style={{ background: "#E5E7EB" }} />
          </div>
        )}
      </div>

      {/* AI-generated impact summary */}
      <div className="px-4 py-3">
        {analysis ? (
          analysis.summary ? (
            <div style={{ background: "#FFFBEB", borderLeft: "3px solid #F59E0B", padding: "8px 12px", borderRadius: "6px" }}>
              <p className="text-sm text-gray-700 leading-relaxed">{analysis.summary}</p>
            </div>
          ) : (
            <p className="text-[13px] italic text-[#9AA0A6]">No direct impact identified from this article.</p>
          )
        ) : (
          <div className="animate-pulse">
            <div style={{ height: "12px", background: "#F3F4F6", borderRadius: "4px", marginBottom: "6px" }} />
            <div style={{ height: "12px", background: "#F3F4F6", borderRadius: "4px", width: "75%" }} />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Diligence Cards (Claude extraction + skeleton) ───────────────────────────

function SkeletonCard() {
  return (
    <div className="rounded-md bg-white animate-pulse"
      style={{ border: "1px solid #ECEEF0", padding: "14px 16px", display: "flex", alignItems: "flex-start", gap: "12px" }}>
      <div style={{ flexShrink: 0, width: "28px", height: "28px", borderRadius: "50%", background: "#E5E7EB" }} />
      <div style={{ flex: 1 }}>
        <div style={{ height: "12px", background: "#E5E7EB", borderRadius: "4px", marginBottom: "6px", width: "90%" }} />
        <div style={{ height: "12px", background: "#E5E7EB", borderRadius: "4px", width: "65%" }} />
      </div>
    </div>
  );
}

function DiligenceQuestionCards({ diligenceText, storyId }) {
  // undefined = loading, null = hide (0 questions), array = show
  const [questions, setQuestions] = useState(undefined);

  useEffect(() => {
    if (!diligenceText?.trim()) { setQuestions(null); return; }
    setQuestions(undefined); // reset to loading
    fetch(`${API}/format/diligence`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ story_id: storyId, content: diligenceText }),
    })
      .then(r => r.json())
      .then(data => {
        if (data.fallback) {
          // Fall back to regex parsing
          const fallback = parseDiligenceQuestions(diligenceText);
          setQuestions(fallback.length ? fallback : null);
        } else {
          setQuestions(data.questions?.length ? data.questions : null);
        }
      })
      .catch(() => {
        const fallback = parseDiligenceQuestions(diligenceText);
        setQuestions(fallback.length ? fallback : null);
      });
  }, [diligenceText, storyId]);

  if (!diligenceText?.trim() || questions === null) return null;

  return (
    <div className="mt-5">
      <div style={{ marginBottom: "14px" }}>
        <p style={{ fontSize: "11px", fontWeight: 600, color: "#9AA0A6", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "4px" }}>
          Key Diligence Questions
        </p>
        <p style={{ fontSize: "13px", fontWeight: 600, color: "#1f2937" }}>
          Questions to investigate before your next move
        </p>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {questions === undefined ? (
          // Loading skeleton
          [1, 2, 3].map(i => <SkeletonCard key={i} />)
        ) : (
          questions.map((q, i) => <DiligenceCard key={i} index={i + 1} question={q} />)
        )}
      </div>
    </div>
  );
}

function DiligenceCard({ index, question }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      className="rounded-md bg-white"
      style={{
        border: hovered ? "1px solid #DCDFE2" : "1px solid #ECEEF0",
        borderLeft: hovered ? "4px solid #97C8A6" : "4px solid transparent",
        padding: "14px 16px",
        boxShadow: hovered ? "0 4px 12px rgba(0,0,0,0.08)" : "0 1px 3px rgba(0,0,0,0.04)",
        transition: "box-shadow 150ms ease, border-color 150ms ease",
        cursor: "default",
        display: "flex",
        alignItems: "flex-start",
        gap: "12px",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-testid={`diligence-card-${index}`}
    >
      <span style={{
        flexShrink: 0, width: "28px", height: "28px", borderRadius: "50%",
        background: "#97C8A6", color: "#181B20",
        fontSize: "12px", fontWeight: 700,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        marginTop: "1px",
      }}>
        {index}
      </span>
      <p className="flex-1 text-sm font-medium" style={{ color: "#1f2937", lineHeight: "1.6", margin: 0 }}>
        {question}
      </p>
      <span style={{ color: "#D1D5DB", fontSize: "18px", flexShrink: 0, marginTop: "1px", lineHeight: 1 }}>›</span>
    </div>
  );
}

// ─── Sources Modal ─────────────────────────────────────────────────────────────

function SourcesModal({ story, globalSources, indianSources, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const headline = (story.title || "").length > 40 ? story.title.slice(0, 40) + "…" : story.title;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
      data-testid="sources-modal">
      <div className="bg-white rounded-md shadow-2xl overflow-hidden flex flex-col"
        style={{ width: "680px", maxHeight: "80vh" }}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between px-6 py-4" style={{ borderBottom: "1px solid #DCDFE2", flexShrink: 0 }}>
          <div>
            <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: "18px", fontWeight: 700, color: "#0A0A0A" }}>
              All Sources
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">{headline}</p>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-[#9AA0A6] hover:text-[#0A0A0A] hover:bg-[#F4F5F6] transition-all"
            data-testid="sources-modal-close-x">
            <X size={14} />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-4">
          {globalSources.length > 0 && (
            <div className="mb-6">
              <p style={{ fontSize: "11px", fontWeight: 700, color: "#6B7280", letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: "12px" }}>
                Global Coverage · {globalSources.length} source{globalSources.length !== 1 ? "s" : ""}
              </p>
              <SourceModalTable sources={globalSources} />
            </div>
          )}
          {indianSources.length > 0 && (
            <>
              {globalSources.length > 0 && <div style={{ height: "1px", background: "#ECEEF0", margin: "8px 0 20px" }} />}
              <div>
                <p style={{ fontSize: "11px", fontWeight: 700, color: "#6B7280", letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: "12px" }}>
                  Regional &amp; Other Sources · {indianSources.length} source{indianSources.length !== 1 ? "s" : ""}
                </p>
                <SourceModalTable sources={indianSources} />
              </div>
            </>
          )}
          {globalSources.length === 0 && indianSources.length === 0 && (
            <p className="text-sm text-gray-400 italic text-center py-8">No sources available.</p>
          )}
        </div>
        <div className="flex justify-end px-6 py-3" style={{ borderTop: "1px solid #F0F2F4", flexShrink: 0 }}>
          <button onClick={onClose}
            className="px-5 py-2 rounded-md text-[13px] font-medium text-[#3A3A3A] hover:bg-[#F4F5F6] transition-colors"
            style={{ border: "1px solid #DCDFE2" }}
            data-testid="sources-modal-close-btn">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function SourceModalTable({ sources }) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse" }}>
      <thead>
        <tr style={{ borderBottom: "1px solid #ECEEF0" }}>
          {["Source Name", "Domain", "Type"].map(col => (
            <th key={col} style={{ textAlign: "left", padding: "0 8px 8px", fontSize: "10px", fontWeight: 700, color: "#9AA0A6", letterSpacing: "0.06em", textTransform: "uppercase" }}>
              {col}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sources.map((art, i) => (
          <tr key={art.article_id || i} style={{ borderBottom: "1px solid #F9FAFB" }} className="hover:bg-[#FAFAFA] transition-colors">
            <td style={{ padding: "10px 8px" }}>
              <div>
                <span className="font-semibold text-[13px] text-[#0A0A0A]">{prettifySourceName(art.source_name)}</span>
                {art.article_url && (
                  <a href={art.article_url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1 mt-0.5 hover:underline"
                    style={{ fontSize: "11px", color: "#6B7280" }}>
                    {truncate(art.headline || art.title, 55)}
                    <ExternalLink size={9} className="flex-shrink-0" />
                  </a>
                )}
              </div>
            </td>
            <td style={{ padding: "10px 8px" }}>
              <span className="text-[12px] text-[#6B7280]">{extractDomain(art.article_url)}</span>
            </td>
            <td style={{ padding: "10px 8px" }}>
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded" style={{ background: "#F0F2F4", color: "#6B7280", whiteSpace: "nowrap" }}>
                {getSourceType(art.source_name)}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─── Source Intelligence ───────────────────────────────────────────────────────

function SourceIntelligence({ allSources, story }) {
  const [showAllGlobal, setShowAllGlobal] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const globalSources = allSources.filter(art => !isIndianSource(art.source_name, art.article_url));
  const indianSources = allSources.filter(art => isIndianSource(art.source_name, art.article_url));
  const isRegionalOnly = globalSources.length === 0 && indianSources.length > 0;

  const defaultSources = isRegionalOnly
    ? indianSources.slice(0, 3)
    : globalSources.slice(0, showAllGlobal ? globalSources.length : 5);

  const extraGlobal = globalSources.length - 5;
  const hasMoreGlobal = !isRegionalOnly && globalSources.length > 5 && !showAllGlobal;

  if (allSources.length === 0) return <p style={{ fontSize: "12px", color: "#9AA0A6", fontStyle: "italic" }}>No sources available.</p>;

  return (
    <>
      {isRegionalOnly && <p className="text-xs italic text-gray-400 mb-3">Regional coverage only</p>}
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        {defaultSources.map((art, i) => <SourceRow key={art.article_id || i} art={art} />)}
      </div>
      {hasMoreGlobal && (
        <button onClick={() => setShowAllGlobal(true)}
          className="mt-3 font-medium hover:underline"
          style={{ color: "#6B7280", fontSize: "12px", display: "block" }}
          data-testid="more-global-sources-btn">
          +{extraGlobal} more global source{extraGlobal !== 1 ? "s" : ""}
        </button>
      )}
      {allSources.length > 0 && (
        <button onClick={() => setShowModal(true)}
          className="mt-4 w-full py-2 rounded-md text-[12px] font-medium transition-colors hover:bg-[#F0F2F4]"
          style={{ border: "1px solid #DCDFE2", color: "#3A3A3A", background: "#FFFFFF" }}
          data-testid="view-all-sources-btn">
          View All Sources ({allSources.length})
        </button>
      )}
      {story.summary_source && (
        <p className="text-[11px] text-[#9AA0A6] mt-3 pt-3" style={{ borderTop: "1px solid #DCDFE2" }}>
          Analysis by: <span className="font-semibold text-[#6B7280]">{story.summary_source}</span>
        </p>
      )}
      {showModal && (
        <SourcesModal story={story} globalSources={globalSources} indianSources={indianSources} onClose={() => setShowModal(false)} />
      )}
    </>
  );
}

function SourceRow({ art }) {
  const name = prettifySourceName(art.source_name);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
      <span className="font-bold text-[12px] text-[#3A3A3A]">{name}</span>
      {(art.article_url || art.headline || art.title) && (
        art.article_url ? (
          <a href={art.article_url} target="_blank" rel="noopener noreferrer"
            className="flex items-start gap-1 hover:underline"
            style={{ color: "#4B5563", fontSize: "12px", lineHeight: "1.4" }}>
            <span>{truncate(art.headline || art.title, 65)}</span>
            <ExternalLink size={10} className="flex-shrink-0 mt-0.5 text-[#9AA0A6]" />
          </a>
        ) : (
          <span style={{ color: "#4B5563", fontSize: "12px", lineHeight: "1.4" }}>
            {truncate(art.headline || art.title, 65)}
          </span>
        )
      )}
    </div>
  );
}

// ─── Recommended Actions Component ─────────────────────────────────────────────

function RecommendedActions({ story, hideEngageEscalate = false }) {
  const [draftModal, setDraftModal] = useState(null); // { type: 'ceo'|'cio'|'dept'|'ic', content: string }
  const [researchQuestion, setResearchQuestion] = useState("");
  const [researchLoading, setResearchLoading] = useState(false);
  const [researchResult, setResearchResult] = useState(null);
  const [flagging, setFlagging] = useState(false);
  const [flaggedIds, setFlaggedIds] = useState(new Set());
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showUnflagConfirm, setShowUnflagConfirm] = useState(false);
  const [unflagTargets, setUnflagTargets] = useState([]);

  // Extract key info from story for drafts
  const storyTitle = story?.title || "Recent Development";
  const portfolioCompanies = (story?.portfolio_impact || []).map(p => p.company_name).filter(Boolean);
  const primaryCompany = portfolioCompanies[0] || "the portfolio company";
  const keyFacts = story?.snippet || story?.description?.slice(0, 200) || "";
  const category = story?.primary_tag || "Market Update";

  // Deduplicated list of companies in this story
  const companies = useMemo(() => {
    const seen = new Set();
    return (story?.portfolio_impact || [])
      .filter(p => {
        const id = p.company_permalink || p.company_id || "";
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      })
      .map(p => ({
        id: p.company_permalink || p.company_id,
        name: p.company_name,
      }));
  }, [story?.portfolio_impact]);

  // Check which companies are already flagged on mount
  useEffect(() => {
    const checkFlags = async () => {
      try {
        const res = await fetch(`${API}/companies/flagged`);
        if (res.ok) {
          const data = await res.json();
          const set = new Set((data.flagged_companies || []).map(fc => fc.company_id));
          setFlaggedIds(set);
        }
      } catch {}
    };
    checkFlags();
  }, []);

  const toggleSelect = (companyId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(companyId)) next.delete(companyId);
      else next.add(companyId);
      return next;
    });
  };

  const handleFlagAction = async () => {
    if (flagging || selectedIds.size === 0) return;

    const toFlag = [];
    const toUnflag = [];
    for (const id of selectedIds) {
      if (flaggedIds.has(id)) toUnflag.push(id);
      else toFlag.push(id);
    }

    // Flag new companies immediately
    if (toFlag.length > 0) {
      setFlagging(true);
      const sourceFeed = (() => { try { return sessionStorage.getItem("wokelo_last_feed") || "portfolio"; } catch { return "portfolio"; } })();
      for (const id of toFlag) {
        const company = companies.find(c => c.id === id);
        try {
          const res = await fetch(`${API}/companies/flag`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              company_id: id,
              company_name: company?.name || "",
              story_id: story?.brief_id || "",
              story_title: storyTitle,
              source_feed: sourceFeed,
            }),
          });
          if (res.ok) setFlaggedIds(prev => new Set([...prev, id]));
        } catch {}
      }
      setFlagging(false);
    }

    // If any selected are already flagged, show unflag confirmation
    if (toUnflag.length > 0) {
      setUnflagTargets(toUnflag);
      setShowUnflagConfirm(true);
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleUnflagConfirm = async () => {
    setShowUnflagConfirm(false);
    setFlagging(true);
    for (const id of unflagTargets) {
      try {
        const res = await fetch(`${API}/companies/unflag/${id}`, { method: "DELETE" });
        if (res.ok) {
          setFlaggedIds(prev => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
        }
      } catch {}
    }
    setUnflagTargets([]);
    setSelectedIds(new Set());
    setFlagging(false);
  };

  const handleUnflagCancel = () => {
    setShowUnflagConfirm(false);
    setUnflagTargets([]);
    setSelectedIds(new Set());
  };

  // How many selected are new vs already flagged
  const selectedNewCount = [...selectedIds].filter(id => !flaggedIds.has(id)).length;
  const selectedFlaggedCount = [...selectedIds].filter(id => flaggedIds.has(id)).length;
  const allCompanyIds = companies.map(c => c.id);
  const allFlagged = allCompanyIds.length > 0 && allCompanyIds.every(id => flaggedIds.has(id));
  const unflagTargetNames = unflagTargets.map(id => companies.find(c => c.id === id)?.name || id);

  // Generate draft email content via AI
  const [draftLoading, setDraftLoading] = useState(false);

  const handleDraftClick = async (type) => {
    setDraftLoading(true);
    setDraftModal({ type, subject: "", body: "", loading: true });
    
    const impactDescriptions = (story?.portfolio_impact || [])
      .map(p => p.impact_description).filter(Boolean).join("; ");
    
    try {
      const response = await fetch(`${API}/draft/email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          recipient: type,
          company_name: primaryCompany,
          story_title: storyTitle,
          story_summary: keyFacts,
          portfolio_impact: impactDescriptions,
          category: category,
          all_companies: portfolioCompanies.length > 0 ? portfolioCompanies : [primaryCompany],
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setDraftModal({ type, subject: data.subject, body: data.body, loading: false });
      } else {
        const draft = generateStaticDraft(type);
        setDraftModal({ type, ...draft, loading: false });
      }
    } catch {
      const draft = generateStaticDraft(type);
      setDraftModal({ type, ...draft, loading: false });
    } finally {
      setDraftLoading(false);
    }
  };
  
  // Static fallback (used if AI fails)
  const generateStaticDraft = (recipient) => {
    const companyList = portfolioCompanies.length > 1 
      ? portfolioCompanies.slice(0, -1).join(", ") + " and " + portfolioCompanies.slice(-1)
      : primaryCompany;

    if (recipient === "ceo") {
      return {
        subject: `${category} — ${primaryCompany}`,
        body: `${storyTitle}\n\n${keyFacts}\n\nWhat is our current exposure here, and how are you thinking about the near-term impact on operations?\n\nBest,`
      };
    } else if (recipient === "cio") {
      return {
        subject: `Market Intelligence: ${category} — Portfolio Implications`,
        body: `${storyTitle}\n\n${keyFacts}\n\nAffected: ${companyList}\n\nWhat adjustments, if any, should we be considering to our current positioning?\n\nBest,`
      };
    } else if (recipient === "dept") {
      return {
        subject: `${category} — Cross-Functional Coordination`,
        body: `${storyTitle}\n\n${keyFacts}\n\nAffected: ${companyList}\n\nPlease review exposure and share any immediate concerns by EOD.\n\nBest,`
      };
    } else if (recipient === "ic") {
      return {
        subject: `IC Memo: ${category}`,
        body: `INVESTMENT COMMITTEE MEMORANDUM\nDate: ${new Date().toLocaleDateString()}\n\n${storyTitle}\n\n${keyFacts}\n\nAffected: ${companyList}\n\nRecommended: exposure analysis within 48 hours, management engagement, precedent review.`
      };
    }
    return { subject: "", body: "" };
  };

  const handleResearchSubmit = async () => {
    if (!researchQuestion.trim()) return;
    setResearchLoading(true);
    setResearchResult(null);
    
    setTimeout(() => {
      setResearchResult(`Based on the story "${storyTitle}", here are the key findings related to your question:\n\n${researchQuestion}\n\n• Analysis of market dynamics and competitive landscape\n• Review of historical precedents and outcomes\n• Assessment of potential scenarios and probabilities\n• Recommended monitoring parameters\n\nThis analysis draws from the current story context and general market intelligence.`);
      setResearchLoading(false);
    }, 1500);
  };

  return (
    <div className="mt-5" data-testid="recommended-actions-section">
      <SectionLabel>Recommended Actions</SectionLabel>
      
      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        
        {/* MONITOR Block — Multi-company flagging */}
        <div 
          className="rounded-md"
          style={{ border: "1px solid #E5E7EB", padding: "16px", background: allFlagged ? "#F0FDF4" : "transparent" }}
          data-testid="action-monitor"
        >
          <span 
            className="inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded mb-2"
            style={{ background: allFlagged ? "#DCFCE7" : "#EEF4FF", color: allFlagged ? "#16A34A" : "#3B6CF5" }}
          >
            {allFlagged ? "All Flagged" : "Monitor"}
          </span>
          <p className="font-semibold text-[14px] text-[#0A0A0A] mb-1">
            {companies.length > 1 ? "Select companies to flag for weekly review" : "Flag for next weekly review"}
          </p>
          <p className="text-[12px] text-[#6B7280] mb-3">
            {companies.length > 1 ? "Choose one or more companies from this story" : "No immediate action required. Track updates."}
          </p>

          {/* Company checkboxes */}
          <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginBottom: "12px" }}>
            {companies.map(c => {
              const isFlagged = flaggedIds.has(c.id);
              const isSelected = selectedIds.has(c.id);
              return (
                <label
                  key={c.id}
                  className="flex items-center gap-3 rounded-md cursor-pointer transition-colors"
                  style={{ 
                    padding: "10px 12px",
                    border: isSelected ? "1px solid #3B6CF5" : isFlagged ? "1px solid #16A34A" : "1px solid #E5E7EB",
                    background: isSelected ? "#EFF6FF" : isFlagged ? "#F0FDF4" : "#FFFFFF",
                  }}
                  data-testid={`flag-company-${c.id}`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleSelect(c.id)}
                    className="accent-blue-600 w-4 h-4 flex-shrink-0"
                    data-testid={`flag-checkbox-${c.id}`}
                  />
                  <CompanyLogo name={c.name} permalink={c.id} size={20} />
                  <span className="text-[13px] font-medium text-[#0A0A0A] flex-1 truncate">{c.name}</span>
                  {isFlagged && (
                    <span 
                      className="text-[10px] font-semibold uppercase px-2 py-0.5 rounded flex-shrink-0"
                      style={{ background: "#DCFCE7", color: "#16A34A" }}
                    >
                      Flagged
                    </span>
                  )}
                </label>
              );
            })}
          </div>

          {/* Action button */}
          <button
            onClick={handleFlagAction}
            disabled={flagging || selectedIds.size === 0}
            className="w-full flex items-center justify-center gap-2 py-2 rounded-md text-[13px] font-semibold transition-colors"
            style={{ 
              background: selectedIds.size === 0 ? "#F3F4F6" : selectedFlaggedCount > 0 && selectedNewCount === 0 ? "#FEF2F2" : "#EEF4FF",
              color: selectedIds.size === 0 ? "#9CA3AF" : selectedFlaggedCount > 0 && selectedNewCount === 0 ? "#DC2626" : "#3B6CF5",
              border: selectedIds.size === 0 ? "1px solid #E5E7EB" : selectedFlaggedCount > 0 && selectedNewCount === 0 ? "1px solid #FCA5A5" : "1px solid #93C5FD",
              cursor: selectedIds.size === 0 ? "not-allowed" : "pointer",
            }}
            data-testid="flag-selected-btn"
          >
            {flagging ? (
              <RefreshCw size={14} className="animate-spin" />
            ) : (
              <Flag size={14} />
            )}
            {flagging 
              ? "Processing..." 
              : selectedIds.size === 0 
                ? "Select companies above" 
                : selectedFlaggedCount > 0 && selectedNewCount === 0
                  ? `Remove ${selectedFlaggedCount} from flagged`
                  : selectedFlaggedCount > 0
                    ? `Flag ${selectedNewCount} & remove ${selectedFlaggedCount}`
                    : `Flag ${selectedNewCount} selected`
            }
          </button>
        </div>

        {/* Unflag Confirmation Dialog */}
        {showUnflagConfirm && (
          <div 
            className="rounded-md"
            style={{ border: "1px solid #FCA5A5", padding: "16px", background: "#FEF2F2" }}
            data-testid="unflag-confirm-dialog"
          >
            <p className="font-semibold text-[14px] text-[#0A0A0A] mb-1">
              Remove {unflagTargetNames.length === 1 ? unflagTargetNames[0] : `${unflagTargetNames.length} companies`} from flagged list?
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", marginBottom: "12px" }}>
              {unflagTargetNames.map((name, i) => (
                <p key={i} className="text-[12px] text-[#6B7280] flex items-center gap-2">
                  <span style={{ color: "#DC2626" }}>×</span> {name}
                </p>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleUnflagConfirm}
                className="px-4 py-1.5 rounded text-[12px] font-semibold text-white transition-colors hover:opacity-90"
                style={{ background: "#DC2626" }}
                data-testid="unflag-confirm-btn"
              >
                Confirm
              </button>
              <button
                onClick={handleUnflagCancel}
                className="px-4 py-1.5 rounded text-[12px] font-semibold text-[#4B5563] transition-colors hover:bg-[#F3F4F6]"
                style={{ border: "1px solid #D1D5DB", background: "#FFFFFF" }}
                data-testid="unflag-cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* ENGAGE Block — hidden for watchlist stories */}
        {!hideEngageEscalate && (
        <div 
          className="rounded-md"
          style={{ border: "1px solid #E5E7EB", padding: "16px" }}
          data-testid="action-engage"
        >
          <span 
            className="inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded mb-2"
            style={{ background: "#EEF4FF", color: "#3B6CF5" }}
          >
            Engage
          </span>
          <p className="font-semibold text-[14px] text-[#0A0A0A] mb-3">Trigger call/memo to Portfolio Co.</p>
          
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <button 
              onClick={() => handleDraftClick("ceo")}
              className="flex items-center justify-between w-full px-3 py-2.5 rounded-md text-left transition-colors hover:bg-[#F9FAFB]"
              style={{ border: "1px solid #E5E7EB" }}
              data-testid="draft-ceo"
            >
              <span className="text-[13px] text-[#374151]">Draft email to CEO / Leadership</span>
              <Mail size={16} className="text-[#9AA0A6]" />
            </button>
          </div>
        </div>
        )}

        {/* ESCALATE Block — hidden for watchlist stories */}
        {!hideEngageEscalate && (
        <div 
          className="rounded-md"
          style={{ border: "1px solid #FECACA", background: "#FEF2F2", padding: "16px" }}
          data-testid="action-escalate"
        >
          <span 
            className="inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded mb-2"
            style={{ background: "#FEE2E2", color: "#DC2626" }}
          >
            Escalate
          </span>
          <p className="font-semibold text-[14px] text-[#0A0A0A] mb-3">Route to Investment Committee</p>
          
          <button 
            onClick={() => handleDraftClick("ic")}
            className="flex items-center justify-between w-full px-3 py-2.5 rounded-md text-left transition-colors hover:bg-[#FEE2E2]"
            style={{ border: "1px solid #FECACA", background: "#FFFFFF" }}
            data-testid="draft-ic"
          >
            <div>
              <span className="text-[13px] text-[#374151] block">Draft memo to IC</span>
              <span className="text-[11px] text-[#9AA0A6]"></span>
            </div>
            <Mail size={18} className="text-[#3B6CF5]" />
          </button>
        </div>
        )}

        {/* RESEARCH Block */}
        <div 
          className="rounded-md"
          style={{ border: "1px solid #E5E7EB", padding: "16px" }}
          data-testid="action-research"
        >
          <span 
            className="inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded mb-2"
            style={{ background: "#F3F4F6", color: "#6B7280" }}
          >
            Research
          </span>
          <p className="font-semibold text-[14px] text-[#0A0A0A] mb-3">Additional Research</p>
          
          <textarea
            value={researchQuestion}
            onChange={(e) => setResearchQuestion(e.target.value)}
            placeholder="Ask a specific research question..."
            className="w-full px-3 py-2.5 rounded-md text-[13px] resize-none transition-colors focus:outline-none focus:ring-2 focus:ring-[#3B6CF5] focus:ring-opacity-30"
            style={{ border: "1px solid #E5E7EB", minHeight: "70px" }}
            data-testid="research-input"
          />
          
          <button 
            onClick={handleResearchSubmit}
            disabled={researchLoading || !researchQuestion.trim()}
            className="w-full mt-3 py-2.5 rounded-md text-[13px] font-semibold text-white transition-colors disabled:opacity-50"
            style={{ background: "#374151" }}
            data-testid="generate-research-btn"
          >
            {researchLoading ? "Generating..." : "Generate Research"}
          </button>

          {researchResult && (
            <div className="mt-3 p-3 rounded-md text-[12px] text-[#374151] whitespace-pre-wrap" style={{ background: "#F9FAFB", border: "1px solid #E5E7EB" }}>
              {researchResult}
            </div>
          )}
        </div>
      </div>

      {/* Draft Modal */}
      {draftModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[100] p-4" onClick={() => !draftModal.loading && setDraftModal(null)}>
          <div 
            className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: "1px solid #E5E7EB" }}>
              <h3 className="font-semibold text-[16px] text-[#0A0A0A]">
                {draftModal.type === "ic" ? "Investment Committee Memo" : `Draft Email to ${draftModal.type.toUpperCase()}`}
              </h3>
              <button onClick={() => setDraftModal(null)} className="p-1 hover:bg-[#F3F4F6] rounded">
                <X size={18} className="text-[#6B7280]" />
              </button>
            </div>
            
            <div className="flex-1 overflow-auto px-6 py-4">
              {draftModal.loading ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="w-8 h-8 border-2 border-[#3B6CF5] border-t-transparent rounded-full animate-spin mb-4" />
                  <p className="text-[13px] text-[#6B7280]">Drafting with AI...</p>
                </div>
              ) : (
                <>
                  <div className="mb-4">
                    <label className="block text-[11px] font-semibold text-[#6B7280] uppercase tracking-wider mb-1">Subject</label>
                    <input 
                      type="text" 
                      value={draftModal.subject}
                      readOnly
                      className="w-full px-3 py-2 rounded-md text-[13px] bg-[#F9FAFB]"
                      style={{ border: "1px solid #E5E7EB" }}
                      data-testid="draft-subject"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-[#6B7280] uppercase tracking-wider mb-1">Body</label>
                    <textarea 
                      value={draftModal.body}
                      readOnly
                      className="w-full px-3 py-2 rounded-md text-[13px] bg-[#F9FAFB] resize-none leading-relaxed"
                      style={{ border: "1px solid #E5E7EB", minHeight: "300px" }}
                      data-testid="draft-body"
                    />
                  </div>
                </>
              )}
            </div>
            
            {!draftModal.loading && (
              <div className="flex justify-end gap-3 px-6 py-4" style={{ borderTop: "1px solid #E5E7EB" }}>
                <button 
                  onClick={() => setDraftModal(null)}
                  className="px-4 py-2 rounded-md text-[13px] font-medium text-[#374151] hover:bg-[#F3F4F6] transition-colors"
                  style={{ border: "1px solid #E5E7EB" }}
                >
                  Close
                </button>
                <button 
                  onClick={() => {
                    navigator.clipboard.writeText(`Subject: ${draftModal.subject}\n\n${draftModal.body}`);
                    setDraftModal(null);
                  }}
                  className="px-4 py-2 rounded-md text-[13px] font-medium text-white transition-colors"
                  style={{ background: "#3B6CF5" }}
                  data-testid="draft-copy-btn"
                >
                  Copy to Clipboard
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function StoryDeepDive() {
  const { storyId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [story, setStory] = useState(() => {
    const stateStory = location.state?.story;
    if (stateStory && String(stateStory.brief_id) === String(storyId)) return stateStory;
    return getAllCachedStories().find(s => String(s.brief_id) === String(storyId)) || null;
  });
  // True when story wasn't in state/cache and we're fetching from API
  const [storyLoading, setStoryLoading] = useState(() => story === null);

  useEffect(() => {
    if (!storyLoading) return;
    fetch(`${API}/stories/${storyId}`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => { setStory(data?.story || null); setStoryLoading(false); })
      .catch(() => setStoryLoading(false));
  }, [storyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const [activeTab, setActiveTab] = useState("brief");

  const portfolioImpact = story?.portfolio_impact || [];
  const hasPortfolio = portfolioImpact.length > 0;
  const isFromWatchlist = sessionStorage.getItem("wokelo_last_feed") === "watchlist";

  // Article-anchored portfolio impact analysis — single source of truth for sentiment + right-side card
  const [portfolioAnalysis, setPortfolioAnalysis] = useState(null);

  useEffect(() => {
    if (!hasPortfolio || !story?.brief_id) return;
    const firstCompany = portfolioImpact.find(p => p.company_name);
    if (!firstCompany) return;

    const storyId = String(story.brief_id);
    const cacheKey = `pia_v1_${storyId}_${firstCompany.company_name}`;
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) { setPortfolioAnalysis(JSON.parse(cached)); return; }
    } catch {}

    fetch(`${API}/portfolio-impact/analyze`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        story_id: storyId,
        company_name: firstCompany.company_name,
        story_title: story.title || "",
        story_summary: story.description || story.snippet || "",
      }),
    })
      .then(r => r.json())
      .then(data => {
        const result = { sentiment: data.sentiment || "NEUTRAL", summary: data.summary || "" };
        setPortfolioAnalysis(result);
        try { localStorage.setItem(cacheKey, JSON.stringify(result)); } catch {}
      })
      .catch(() => setPortfolioAnalysis({ sentiment: "NEUTRAL", summary: "" }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [story?.brief_id, hasPortfolio]);
  const hasDiligence = Boolean(story?.diligence_area?.trim());

  // Group portfolio impacts by company name to merge duplicates
  const mergedImpacts = useMemo(() => {
    const grouped = {};
    for (const impact of portfolioImpact) {
      const key = impact.company_name || "Unknown";
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(impact);
    }
    return Object.values(grouped);
  }, [portfolioImpact]);

  const allSources = useMemo(() =>
    deduplicateSources(story?.source_articles || story?.articles || []),
    [story]
  );

  if (storyLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#FFFFFF" }}>
        <div className="space-y-3 w-72 animate-pulse">
          {[100, 80, 90, 65, 75].map((w, i) => (
            <div key={i} className="h-3 rounded" style={{ width: `${w}%`, background: "#E5E7EB" }} />
          ))}
        </div>
      </div>
    );
  }

  if (!story) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center" style={{ background: "#FFFFFF" }}>
        <AlertCircle size={40} className="text-[#9AA0A6] mb-4" />
        <p className="text-[16px] font-semibold text-[#3A3A3A]">Story not found</p>
        <p className="text-[13px] text-[#9AA0A6] mt-1 mb-6">It may have been removed from the cache.</p>
        <button onClick={() => navigate("/briefing")}
          className="flex items-center gap-2 text-[13px] font-medium text-[#0A0A0A] hover:underline">
          <ArrowLeft size={14} /> Back to Briefing
        </button>
      </div>
    );
  }

  const isActive = !story.closed_at;
  const briefText = getBriefText(story.description);

  return (
    <div style={{ background: "#FFFFFF", minHeight: "100%", padding: "0 0 60px" }}>

      {/* Back link */}
      <div style={{ padding: "20px 32px 0", maxWidth: "1400px", margin: "0 auto" }}>
        <button onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-[13px] text-[#6B7280] hover:text-[#0A0A0A] transition-colors"
          data-testid="back-to-briefing">
          <ArrowLeft size={14} />
          Back to Briefing
        </button>
      </div>

      {/* ── Two-Column Layout ── */}
      <div style={{ padding: "20px 32px 0", maxWidth: "1400px", margin: "0 auto" }}>
        <div style={{ display: "flex", gap: "24px" }}>

          {/* ── Left Column: Main Content (65%) ── */}
          <div style={{ flex: "1 1 0%", minWidth: 0 }}>

            {/* Summary Card */}
            <div className="bg-white rounded-md mb-5" style={{ padding: "32px", boxShadow: "0 1px 4px rgba(0,0,0,0.1)" }}>

              {/* Headline */}
              <h1 className="font-bold text-[#0A0A0A] leading-tight mb-4"
                style={{ fontFamily: "'Playfair Display', serif", fontSize: "36px", lineHeight: "1.2" }}
                data-testid="story-headline">
                {story.title}
              </h1>

              {/* Meta row: First reported, Last updated, Sources, Developing status */}
              <div className="flex flex-wrap items-center gap-5 text-[12px] text-[#9AA0A6] mb-5 pb-5"
                style={{ borderBottom: "1px solid #DCDFE2" }}>
                <span className="flex items-center gap-1">
                  <Clock size={12} />
                  First reported: <strong className="text-[#3A3A3A] ml-1">{formatDate(story.first_seen_at)}</strong>
                </span>
                <span className="flex items-center gap-1">
                  <RefreshCw size={12} />
                  Last updated: <strong className="text-[#3A3A3A] ml-1">{formatDate(story.updated_at)}</strong>
                </span>
                {isActive && (
                  <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-[#97C8A6] inline-block animate-pulse" />
                    <span style={{ color: "#2A5B46", fontWeight: 600 }}>Developing</span>
                  </span>
                )}
              </div>

              {/* Story Info Badges: Category, Sentiment, Portfolio Impact */}
              <StoryInfoBadges story={story} isFromWatchlist={isFromWatchlist} portfolioAnalysis={portfolioAnalysis} />

              {/* Tabs */}
              <div className="flex gap-1 mb-5">
                {["brief", "full"].map(tab => (
                  <button key={tab} onClick={() => setActiveTab(tab)}
                    data-testid={`tab-${tab}`}
                    className="px-4 py-2 rounded-md text-[13px] font-medium transition-colors"
                    style={{
                      background: activeTab === tab ? "#0A0A0A" : "transparent",
                      color: activeTab === tab ? "#ffffff" : "#6B7280",
                      border: activeTab === tab ? "none" : "1px solid #DCDFE2",
                    }}>
                    {tab === "brief" ? "Brief Summary" : "Full Analysis"}
                  </button>
                ))}
              </div>

              {/* Tab content — Claude-formatted HTML */}
              <div data-testid="tab-content">
                {activeTab === "brief" ? (
                  <>
                    <FormattedSummary text={briefText} type="brief" storyId={String(story.brief_id || "")} />
                    <button onClick={() => setActiveTab("full")}
                      className="mt-3 text-[13px] font-medium hover:underline block"
                      style={{ color: "#3A3A3A" }}
                      data-testid="read-full-analysis-link">
                      Read full analysis →
                    </button>
                  </>
                ) : (
                  <div style={{ maxHeight: "400px", overflowY: "auto" }} className="pr-2">
                    <FormattedSummary text={story.description} type="full" storyId={String(story.brief_id || "")} />
                    {!story.description && <p className="text-sm text-gray-400 italic">No detailed analysis available.</p>}
                  </div>
                )}
              </div>
            </div>

            {/* Divider */}
            <div style={{ height: "1px", background: "#DCDFE2", marginBottom: "20px" }} />

            {/* Investor Takeaway — amber container, structured or plain-text fallback */}
            <div className="rounded-md mb-5"
              style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden" }}
              data-testid="investor-takeaway-section">
              <div style={{ borderLeft: "4px solid #FBD04B", background: "#FFFBEB", borderRadius: "6px", padding: "24px 28px" }}>
                <p style={{ fontSize: "11px", fontWeight: 600, color: "#92400E", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "12px" }}>
                  Investor Takeaway
                </p>
                <StructuredTakeaway story={story} storyId={String(story.brief_id || "")} />
              </div>
            </div>

          </div>

          {/* ── Right Column: Context Sidebar (35%) ── */}
          <div
            style={{
              flex: "0 0 35%",
              minWidth: 0,
            }}
          >

            {/* Portfolio Impact — hidden when viewing from Watchlist feed */}
            {hasPortfolio && !isFromWatchlist && (
              <div data-testid="portfolio-impact-section" className="mb-1">
                <SectionLabel>Portfolio Impact</SectionLabel>
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  {mergedImpacts.map((impacts, i) => (
                    <PortfolioImpactCard
                      key={impacts[0].company_name || i}
                      impacts={impacts}
                      analysis={i === 0 ? portfolioAnalysis : null}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Watchlist Impact — shown only when viewing from Watchlist feed */}
            {hasPortfolio && isFromWatchlist && (
              <div data-testid="watchlist-impact-section" className="mb-1">
                <SectionLabel>Watchlist Impact</SectionLabel>
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  {mergedImpacts.map((impacts, i) => (
                    <PortfolioImpactCard
                      key={impacts[0].company_name || i}
                      impacts={impacts}
                      analysis={i === 0 ? portfolioAnalysis : null}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Recommended Actions */}
            <RecommendedActions story={story} hideEngageEscalate={isFromWatchlist} />

            {/* Source Intelligence */}
            <div className="mt-5"
              style={{ padding: "22px", background: "#FFFFFF", borderRadius: "6px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", border: "1px solid #ECEEF0" }}
              data-testid="source-intelligence-section">
              <SectionLabel>Source Intelligence</SectionLabel>
              <SourceIntelligence allSources={allSources} story={story} />
            </div>

          </div>

        </div>
      </div>
    </div>
  );
}
